import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { brandPassportUrl, brandPassportDisplay, PASSPORT_BRAND_HOST } from "@/lib/passport";
import { toast } from "sonner";
import { Copy, ExternalLink, Sparkles, ShieldCheck, Upload, Loader2, ArrowRight } from "lucide-react";

export default function PassportCard({ hasAssessment = false }) {
  const { user, setUser, refresh } = useAuth();
  const [editing, setEditing] = useState(false);
  const [unameInput, setUnameInput] = useState("");
  const [check, setCheck] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    bio: "", location: "", website: "", picture: "",
  });
  const fileRef = useRef(null);

  useEffect(() => {
    if (user) setForm({
      bio: user.bio || "",
      location: user.location || "",
      website: user.website || "",
      picture: user.picture || "",
    });
  }, [user]);

  useEffect(() => {
    const v = unameInput.trim().toLowerCase().replace(/^@/, "");
    if (!v) { setCheck(null); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get(`/profile/username-available?u=${encodeURIComponent(v)}`); setCheck(data); }
      catch { setCheck({ available: false, reason: "Error" }); }
    }, 250);
    return () => clearTimeout(t);
  }, [unameInput]);

  if (!user) return null;
  const hasUsername = !!user.username;
  const passportUrl = hasUsername ? brandPassportUrl(user.username) : "";
  const passportDisplay = hasUsername ? brandPassportDisplay(user.username) : "";
  const passportReady = hasUsername && hasAssessment;

  const claimUsername = async () => {
    const v = unameInput.trim().toLowerCase().replace(/^@/, "");
    if (!v) return;
    setBusy(true);
    try {
      const { data } = await api.post("/profile/username", { username: v });
      setUser?.(data.user);
      toast.success(`Passport URL claimed: /c/${data.user.username}`);
      setUnameInput("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  const saveProfile = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/profile", form);
      setUser?.(data.user);
      toast.success("Profile saved.");
      setEditing(false);
      await refresh?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) {
      toast.error("JPG, PNG, or WEBP only."); return;
    }
    if (f.size > 50 * 1024 * 1024) { toast.error("Max 50 MB."); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/profile/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((s) => ({ ...s, picture: data.url }));
      setUser?.({ ...user, picture: data.url });
      toast.success("Profile photo uploaded.");
      await refresh?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(passportUrl); toast.success("Passport link copied."); }
    catch { toast.error("Could not copy."); }
  };

  return (
    <div data-testid="passport-card" className="border border-white/5 bg-[#0c0c0c] p-7 mb-px">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Sparkles size={16} className="text-amber-500" />
            <div className="text-overline">BeatCredsi Passport<span className="text-amber-500">™</span></div>
          </div>
          <p className="text-sm text-neutral-400 max-w-xl">
            Your public producer credibility profile. Shareable URL the industry can look up — your score, status, verified platforms, infrastructure.
          </p>
        </div>
        {passportReady && (
          <Link to={`/c/${user.username}`} target="_blank" rel="noopener noreferrer"
            data-testid="generate-passport-btn"
            className="inline-flex items-center gap-2 px-5 py-3 bg-amber-500 text-black font-semibold hover:brightness-110 transition-all">
            Generate BeatCredsi Passport <ArrowRight size={16} />
          </Link>
        )}
      </div>

      {/* Profile photo + identity strip (always shown when username claimed) */}
      {hasUsername && (
        <div className="flex flex-col md:flex-row items-start gap-5 p-4 bg-[#0A0A0A] border border-white/5 mb-4" data-testid="profile-identity">
          <div className="relative w-20 h-20 border border-amber-500/30 overflow-hidden shrink-0 bg-[#161616]">
            {form.picture ? (
              <img src={form.picture} alt={user.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-amber-500 font-display text-3xl">
                {user.name?.[0]?.toUpperCase() || "B"}
              </div>
            )}
            {uploading && (
              <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                <Loader2 size={20} className="text-amber-500 animate-spin" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-xl text-white">{user.name}</div>
            <div className="font-mono-tech text-xs text-amber-500 mb-2">@{user.username}</div>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={onFile}
              className="hidden" data-testid="photo-upload-input" />
            <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="photo-upload-btn"
              className="inline-flex items-center gap-2 px-3 py-2 border border-white/15 text-xs uppercase tracking-widest font-semibold text-neutral-200 hover:border-amber-500 hover:text-amber-500 transition-colors disabled:opacity-50">
              <Upload size={12} /> {form.picture ? "Replace Photo" : "Upload Photo"}
            </button>
          </div>
          <div className="flex flex-col gap-2 w-full md:w-auto">
            <a href={passportUrl} target="_blank" rel="noopener noreferrer" data-testid="passport-visit-btn"
              className="inline-flex items-center justify-center gap-2 px-3 py-2 border border-amber-500/40 text-amber-500 text-xs font-semibold uppercase tracking-widest hover:bg-amber-500/10 transition-colors">
              <ExternalLink size={12} /> Visit
            </a>
            <button onClick={copyLink} data-testid="passport-copy-btn"
              className="inline-flex items-center justify-center gap-2 px-3 py-2 border border-white/15 text-neutral-200 text-xs font-semibold uppercase tracking-widest hover:border-amber-500 hover:text-amber-500 transition-colors">
              <Copy size={12} /> Copy Link
            </button>
          </div>
        </div>
      )}

      {!hasUsername ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-stretch">
          <div className="md:col-span-2 flex items-stretch bg-[#161616] border border-white/15 focus-within:border-amber-500">
            <span className="px-3 flex items-center text-neutral-500 font-mono-tech text-xs select-none">{PASSPORT_BRAND_HOST}/c/</span>
            <input data-testid="username-input" value={unameInput}
              onChange={(e) => setUnameInput(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""))}
              placeholder="themartiansmg" maxLength={30}
              className="flex-1 bg-transparent px-2 py-3 outline-none text-white text-sm" />
            {check && (
              <span className={`px-3 flex items-center text-xs uppercase tracking-widest font-semibold
                ${check.available ? "text-emerald-400" : "text-rose-400"}`}>
                {check.available ? "Available" : check.reason}
              </span>
            )}
          </div>
          <button onClick={claimUsername} disabled={busy || !check?.available} data-testid="claim-username-btn"
            className="bg-amber-500 text-black font-semibold px-5 py-3 hover:brightness-110 disabled:opacity-40 transition-all">
            {busy ? "Claiming…" : "Claim Passport URL"}
          </button>
        </div>
      ) : (
        <>
          <div className="p-3 bg-[#0A0A0A] border border-white/5 flex items-center gap-3 mb-4" data-testid="passport-url-row">
            <ShieldCheck size={14} className="text-amber-500 shrink-0" />
            <span className="font-mono-tech text-xs text-amber-500 truncate flex-1">{passportDisplay}</span>
            {!hasAssessment && (
              <Link to="/assessment" className="text-xs uppercase tracking-widest font-semibold text-amber-500 hover:underline">
                Take Assessment →
              </Link>
            )}
          </div>
          {!editing ? (
            <button onClick={() => setEditing(true)} data-testid="edit-profile-btn"
              className="text-sm text-amber-500 hover:underline">Edit bio · location · website →</button>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="profile-edit-form">
              <div className="md:col-span-2">
                <label className="text-xs uppercase tracking-widest text-neutral-500 mb-1 block">Bio (max 280)</label>
                <textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} maxLength={280} rows={2}
                  data-testid="bio-input"
                  className="w-full bg-[#161616] border border-white/15 px-3 py-2 text-sm text-white outline-none focus:border-amber-500" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-neutral-500 mb-1 block">Location</label>
                <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="location-input"
                  className="w-full bg-[#161616] border border-white/15 px-3 py-2 text-sm text-white outline-none focus:border-amber-500" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-neutral-500 mb-1 block">Website</label>
                <input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="yoursite.com" data-testid="website-input"
                  className="w-full bg-[#161616] border border-white/15 px-3 py-2 text-sm text-white outline-none focus:border-amber-500" />
              </div>
              <div className="md:col-span-2 flex items-center gap-3">
                <button onClick={saveProfile} disabled={busy} data-testid="save-profile-btn"
                  className="bg-amber-500 text-black font-semibold px-5 py-2.5 hover:brightness-110 disabled:opacity-50 text-sm">
                  {busy ? "Saving…" : "Save"}
                </button>
                <button onClick={() => setEditing(false)} className="text-sm text-neutral-400 hover:text-white">Cancel</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
